import React, { useEffect, useState } from 'react'
import { Button, Nav, Tab, Container, Row, Col } from 'react-bootstrap'
import { connect } from 'react-redux'
import { TRANSACTION_FAILED, TRANSACTION_PROCESSING, transactionInit } from '../../Pricing/actions'
import { LoadingBox } from '../LoadingBox/LoadingBox'
import { ErrorBox } from '../ErrorBox/ErrorBox'
import { PERIOD_MONTH, PERIOD_YEAR, PLANS } from '../../constants/plans'
import PaddlePay from './components/PaddlePay'
import { ERROR_CODE_MAP } from '../../stripe.constants'
import StripePay from './components/Stripe/StripePay'
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { enrollInEmailList, MAIL_LIST_CHECKOUT_DROP } from '../../services/email-list'
import FeatureList from '../UpgradePayModal/components/FeatureList'
import CurrencyDropdown from 'components/CurrencyDropdown/CurrencyDropDown'
import { availableCurrencies } from 'constants/pricing/currency'
import { abandonedCheckout } from 'utils/sendy'
import { useHistory } from 'react-router-dom'
import CustomModal from 'components/CustomModal/CustomModal'

import styles from './PaymentModal.module.css'

const getErrorMessagePayment = ({ declineCode = '', errorCode, accountAlreadyExistsMessage }) => {
  return (
    accountAlreadyExistsMessage ||
    ERROR_CODE_MAP[declineCode] ||
    `Sorry, unable to process your card. Please try again (${errorCode} ${declineCode})`
  )
}

const getOnClickAction = ({ dispatch, accountAlreadyExistsMessage }) => {
  if (accountAlreadyExistsMessage) return () => (window.location.href = '/login')
  return () => dispatch(transactionInit())
}

const getPriceLabel = (plan, period, discountPercent, showMonthlyEquivalent = false, decimals = 0) => {
  const currency = plan.prices[period]?.currency || '$'
  const value = plan.prices[period]?.value || 0
  const originalPrice = plan.prices[period] && `${currency}${value.toFixed(decimals)}`

  if (showMonthlyEquivalent && period === PERIOD_YEAR) {
    const yearlyAmount = parseInt(value)
    const monthlyEquivalent = yearlyAmount / 12
    return `${currency}${monthlyEquivalent}`
  }

  if (discountPercent) {
    const amount = parseInt(value)
    const discount = parseFloat(discountPercent)
    const final = (discount * amount) / 100
    const finalPrice = amount - final

    return (
      <>
        <span style={{ textDecoration: 'line-through' }}>{originalPrice}</span>
        <span className="ps-1">
          {currency}
          {finalPrice.toFixed(2)}
        </span>
      </>
    )
  } else {
    return originalPrice
  }
}

const PaymentModal = ({
  show,
  onHide,
  dispatch,
  declineCode,
  errorCode,
  transactionState,
  email,
  studentData,
  plan,
  periodProp = 'monthly',
  source,
  accountAlreadyExistsMessage,
  accountCreated,
  onCurrencySelected,
  isTrial: isFreeAccount
}) => {
  const [period, setPeriod] = useState(periodProp)
  const isLoading = transactionState === TRANSACTION_PROCESSING
  const isError = transactionState === TRANSACTION_FAILED
  const priceData = plan.prices[period] || {}
  const [errorMessage, setErrorMessage] = useState('')
  const [discountPercent, setDiscountPercent] = useState()
  const history = useHistory()

  useEffect(() => setPeriod(periodProp), [periodProp])

  const handleCouponCodeChange = (e) => {
    setDiscountPercent(e)
  }

  const handleDurationChange = (duration) => {
    setPeriod(duration)
    setErrorMessage('')
  }

  const showCurrencyDropdown = isFreeAccount && plan.id !== PLANS.API.id

  const onClose = () => {
    if (email) {
      enrollInEmailList(email, MAIL_LIST_CHECKOUT_DROP) // Fire & forget
      abandonedCheckout()
    }

    const { pathname } = window.location
    if (pathname === '/manage/subscription/upgrade' || pathname === '/manage/subscription/change') {
      history.push('/manage')
    }

    if (onHide) onHide()
  }

  return (
    <CustomModal
      backdrop="static"
      size="lg"
      show={show}
      onHide={onClose}
      aria-labelledby="contained-modal-title-vcenter"
      dialogClassName={styles.dialog}
      centered
      enforceFocus={false}
      mobileCloseButton={false}
    >
      <Container fluid>
        <Row>
          <Col lg={5} className={`d-none d-lg-block bg-light-grey p-4 ${styles.featureContainer}`}>
            <div className="d-block d-md-none">
              <img onClick={onClose} className="link float-right" src="/delete.png" height="20" />
            </div>
            <h2 className="mb-4 color-purple-2 font-22 ms-3 mt-2">
              <div className="text-dark-blue font-20 text-700">Upgrade to </div>
              <div className="text-light-purple font-48 text-700 line-height-100 mt-2">{plan.label}</div>
              <div className="text-dark-purple font-14 text-500 line-height-120">Tiiny Host Subscription</div>
            </h2>
            <FeatureList planId={plan.id} stage={null} list={[]} />
            <div className={`${styles.toc} d-none d-lg-block`}>
              By using our service you accept our{' '}
              <a href="https://tiiny.host/termsofservice.html" target="_blank" rel="noopener noreferrer">
                <u>terms & conditions</u>
              </a>{' '}
              &{' '}
              <a href="https://tiiny.host/privacypolicy.html" target="_blank" rel="noopener noreferrer">
                <u>privacy policy</u>
              </a>
            </div>
          </Col>
          <Col lg={7} className={`p-4 ${styles.paymentContainer}`}>
            <div>
              <span className="text-dark-blue font-20 text-700">Choose a payment plan:</span>
              <img onClick={onClose} className="link float-right" src="/delete.png" height="20" />
            </div>
            <div className="position-relative">
              <div className={`${isLoading || isError ? 'd-none' : 'd-block'} mb-4`}>
                <div className="px-0 pt-4 pb-2 price_button">
                  {plan.prices.monthly && (
                    <Button
                      className={`w-100 mb-3 p-4 bold ${period === 'monthly' ? styles.priceButton_p : styles.priceButton}`}
                      variant="light"
                      onClick={() => handleDurationChange(PERIOD_MONTH)}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className={`${styles.priceTag} text-darker-blue font-16 mb-0`}>Monthly</h6>
                        <div className={`${styles.priceAmount} text-700 font-32 text-darker-blue line-height-100`}>
                          {getPriceLabel(plan, PERIOD_MONTH)}{' '}
                          <span className={`text-darker-blue line-height-100 font-24`}>/mo</span>
                        </div>
                      </div>
                    </Button>
                  )}

                  {plan.prices.yearly && (
                    <Button
                      className={`w-100 p-4 ${period === PERIOD_YEAR ? styles.priceButton_p : styles.priceButton}`}
                      variant="light"
                      onClick={() => handleDurationChange(PERIOD_YEAR)}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="text-start">
                          <h6 className={`${styles.priceTag} text-darker-blue font-16 text-decoration`}>Annual</h6>
                          {[PLANS.PRO.id, PLANS.PRO_U.id, PLANS.TINY.id].includes(plan.id) && (
                            <span className={`badge bg-success mt-1 ${styles.priceBadge}`}>2 months free</span>
                          )}
                        </div>
                        <div className={`${styles.priceAmount} text-700 font-32 text-darker-blue line-height-100`}>
                          <div>
                            {getPriceLabel(plan, PERIOD_YEAR, 0, true)}{' '}
                            <span className={`text-darker-blue line-height-100 font-24`}>/mo</span>
                          </div>
                          <span className={`text-500 text-grey-black font-14 line-height-19`}>
                            Billed at {getPriceLabel(plan, PERIOD_YEAR)} /yr
                          </span>
                        </div>
                      </div>
                    </Button>
                  )}
                </div>

                <div className={`d-flex justify-content-between align-items-center p-2 mt-3 ${styles.totalContainer}`}>
                  <div>
                    <span className={`text-dark-blue font-20 text-700 line-height-27 d-block`}>TOTAL</span>
                  </div>
                  <div className="d-flex flex-row align-items-center">
                    <span className="text-dark-blue font-20 text-700 line-height-27 me-2">
                      {getPriceLabel(plan, period, discountPercent, false, 2)}
                    </span>
                    {showCurrencyDropdown && (
                      <CurrencyDropdown
                        selectedCurrency={plan.prices.monthly?.currency}
                        onCurrencySelected={onCurrencySelected}
                        availableCurrencies={availableCurrencies}
                        showSymbol={false}
                      />
                    )}
                  </div>
                </div>

                <div className="text-end me-2 mt-1">
                  <a
                    className="text-darker-blue font-12 line-height-16 text-500"
                    target="_blank"
                    href="https://helpdesk.tiiny.host/en/article/how-does-your-7-day-money-back-guarantee-work-ghtpu0/"
                    rel="noreferrer"
                  >
                    <img src="/assets/icons/currency-dollar.svg" className="mb-1" height={14} />
                    &nbsp;7-day money-back guarantee
                  </a>
                </div>
              </div>

              <>
                <Tab.Container defaultActiveKey="card">
                  <Nav variant="pills" className={`flex-row tabs ${isLoading || isError ? 'd-none' : ''}`}>
                    <Nav.Item>
                      <Nav.Link eventKey="card">
                        <h2 className="fs-normal">
                          <img src="/assets/icons/credit-card.svg" className="me-2 opacity-50" height={20} />
                          Card
                        </h2>
                      </Nav.Link>
                    </Nav.Item>
                    {plan.id !== PLANS.API.id && (
                      <Nav.Item>
                        <Nav.Link eventKey="paypal">
                          <h2 className="fs-normal">
                            <img src="/assets/icons/paypal.svg" className="me-2 opacity-50" height={18} />
                            Paypal
                          </h2>
                        </Nav.Link>
                      </Nav.Item>
                    )}
                  </Nav>
                  <Tab.Content>
                    <Tab.Pane eventKey="card">
                      <StripePay
                        className={isLoading || isError ? 'd-none' : 'd-block mt-3'}
                        dispatch={dispatch}
                        priceData={priceData}
                        email={email}
                        studentData={studentData}
                        source={source}
                        productId={plan.id}
                        period={period}
                        accountCreated={accountCreated}
                        handleCouponCodeChange={handleCouponCodeChange}
                        errorMessage={errorMessage}
                        setErrorMessage={setErrorMessage}
                      />
                    </Tab.Pane>

                    {plan.id !== PLANS.API.id && (
                      <Tab.Pane eventKey="paypal" unmountOnExit={false} mountOnEnter={true}>
                        <PaddlePay
                          className={isLoading || isError ? 'd-none' : 'd-block'}
                          dispatch={dispatch}
                          priceId={priceData.id}
                          email={email}
                          studentData={studentData}
                          paddleLink={priceData.paddleLink}
                          paddleId={priceData.paddleId}
                          transactionState={transactionState}
                          source={source}
                          productId={plan.id}
                          period={period}
                          accountCreated={accountCreated}
                        />
                      </Tab.Pane>
                    )}
                  </Tab.Content>
                </Tab.Container>
              </>
              <LoadingBox className={isLoading ? 'd-block' : 'd-none'} message="Processing" />
              {isError && (
                <ErrorBox
                  className="d-block"
                  message={getErrorMessagePayment({
                    errorCode,
                    declineCode,
                    accountAlreadyExistsMessage,
                    paddleLink: priceData.paddleLink
                  })}
                  onClick={getOnClickAction({ dispatch, accountAlreadyExistsMessage })}
                />
              )}
            </div>
          </Col>
        </Row>

        {/* Only on mobile */}
        <div className="d-lg-none op-6 font-small ps-3 pe-3 pb-3">
          By using our service you accept our{' '}
          <a href="https://tiiny.host/termsofservice.html" target="_blank" rel="noopener noreferrer">
            <u>terms & conditions</u>
          </a>{' '}
          &{' '}
          <a href="https://tiiny.host/privacypolicy.html" target="_blank" rel="noopener noreferrer">
            <u>privacy policy</u>
          </a>
        </div>
      </Container>
    </CustomModal>
  )
}

const mapDispatchToProps = (dispatch) => ({ dispatch })
const mapStateToProps = ({ pricing, manage }) => ({
  accountCreated: manage?.userProfile?.created,
  transactionState: pricing?.transactionState,
  declineCode: pricing?.declineCode,
  errorCode: pricing?.errorCode,
  accountAlreadyExistsMessage: pricing?.accountAlreadyExistsMessage,
  isTrial: !manage?.userProfile?.productId
})

export default connect(mapStateToProps, mapDispatchToProps)(withGoogleReCaptcha(PaymentModal))
